import { BitkitAlert } from '@bitrise/bitkit-v2';
import { chakra } from '@chakra-ui/react/styled-system';
import { useEffect, useState } from 'react';

import FileViewer from '@/components/FileViewer/FileViewer';
import { DecoratedArtifact } from '@/models/Artifact';

interface TextContentProps {
  artifact: DecoratedArtifact;
  isOpen: boolean;
}

const TextContent = ({ artifact, isOpen = false }: TextContentProps) => {
  const { downloadUrl, extension, title } = artifact;

  const [isLoading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [hasError, setError] = useState(false);

  useEffect(() => {
    const getFile = async () => {
      try {
        const response = await fetch(downloadUrl);
        if (response.status === 403) {
          setError(true);
        } else {
          const text = await response.text();
          if (extension === 'html') {
            const blob = new Blob([text], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            setContent(url);
          } else {
            setContent(text);
          }
        }
      } catch {
        setError(true);
      }
      setLoading(false);
    };
    if (!content && isOpen) {
      getFile();
    }
  }, [content, downloadUrl, extension, isOpen]);

  if (hasError) {
    return <BitkitAlert variant="critical" messageText="Unknown error" />;
  }

  if (!isLoading && content) {
    if (extension === 'html') {
      return (
        <iframe
          src={content}
          title={title}
          frameBorder="0"
          height="320"
          sandbox="allow-scripts allow-popups"
          width="100%"
        />
      );
    }

    if (extension === 'log' || extension === 'txt' || extension === 'md') {
      return (
        <chakra.textarea
          backgroundColor="background/fixed-contrast"
          border="0"
          borderRadius="8"
          color="text/on-color"
          fontFamily="mono"
          lineHeight="1.5"
          minHeight="20rem"
          padding="24"
          readOnly
          resize="vertical"
          value={content}
          width="100%"
        />
      );
    }

    if (extension === 'plist' || extension === 'xml' || extension === 'json') {
      const language = extension === 'json' ? 'json' : 'xml';
      return <FileViewer isAutoHeight maxHeight={500} language={language} content={content} prettifyJson />;
    }

    return null;
  }

  return <BitkitAlert variant="progress" messageText="Loading..." />;
};

export default TextContent;
